import { isNumber } from 'lodash';

const DEFAULT_CACHE_SIZE = 10 * 1024 * 1024; // 10MB

export class SlidesStore {
    private getSlidesUrl: string;

    private maxCacheSize: number;

    private cache: Map<string, { data: string, lastAccess: number }>;

    private currentCacheSize: number;

    constructor(getSlidesUrl: string, maxCacheSize: number = DEFAULT_CACHE_SIZE) {
        this.getSlidesUrl = getSlidesUrl;
        this.maxCacheSize = maxCacheSize;
        this.cache = new Map();
        this.currentCacheSize = 0;
    }

    async getImage(slideShowId: string | null, slideIndex: number | null): Promise<string | null> {
        if (!slideShowId || !isNumber(slideIndex)) {
            return null;
        }

        const key = this._getKey(slideShowId, slideIndex);

        let image;
        if (this.cache.has(key)) {
            image = this.cache.get(key)?.data || null;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.cache.get(key).lastAccess = Date.now();
        } else {
            image = await this._fetchAndStoreImage({ slideShowId, slideIndex });
        }
        this.preload(slideShowId, slideIndex + 1);

        return image;
    }

    preload(slideShowId: string | null, slideIndex: number | null): void {
        if (!slideShowId || !slideIndex) {
            return;
        }

        const key = this._getKey(slideShowId, slideIndex);
        if (!this.cache.has(key)) {
            this._fetchAndStoreImage({ slideShowId, slideIndex }).catch(() => {
                console.log('Failed to preload image');
            });
        }
    }

    private async _getBase64SlideFromUrl(slideShowId: string, slideIndex: number): Promise<string | null> {
        const url = this.getSlidesUrl
            .replace('{{slideShowId}}', slideShowId)
            .replace('{{slideIndex}}', slideIndex.toString());
        const response = await fetch(url).catch(() => {
            console.log('Failed to fetch image');
        });
        if (!response || response.status !== 200) {
            return null;
        }
        const blob = await response.blob();
        return await this._convertBlobToBase64(blob);
    }

    private _getKey(slideShowId: string, slideIndex: number): string {
        return `${slideShowId}-${slideIndex}`;
    }

    private async _fetchAndStoreImage({ slideShowId, slideIndex }: { slideShowId: string, slideIndex: number }): Promise<string | null> {
        const base64 = await this._getBase64SlideFromUrl(slideShowId, slideIndex);
        if (!base64) {
            return null;
        }

        const key = this._getKey(slideShowId, slideIndex);
        const size = this._getBase64Size(base64);
        this._manageCacheSize(key, size);

        this.cache.set(key, { data: base64, lastAccess: Date.now() });
        return base64;
    }

    private _manageCacheSize(newKey: string, newSize: number): void {
        this.currentCacheSize += newSize;
        if (this.currentCacheSize <= this.maxCacheSize) {
            return;
        }

        const entries = Array.from(this.cache.entries())
            .map(([key, value]) => ({
                key,
                lastAccess: value.lastAccess
            }))
            .sort((a, b) => a.lastAccess - b.lastAccess);

        for (const entry of entries) {
            if (this.currentCacheSize <= this.maxCacheSize) {
                return;
            }
            if (entry.key === newKey) {
                continue;
            }

            const oldSize = this._getBase64Size(this.cache.get(entry.key)?.data);
            this.currentCacheSize -= oldSize;
            this.cache.delete(entry.key);
        }
    }

    private _getBase64Size(base64: string | undefined): number {
        if (!base64) {
            return 0;
        }
        return Math.ceil((base64.length * 3) / 4);
    }

    private _convertBlobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => {
                resolve(reader.result as string);
            };
            reader.readAsDataURL(blob);
        });
    }
}
